import type { InstrumentRunDataRow } from '@/types';

import type { CellErrors } from './types';

const MAX_LIBRARY_GROUP_LENGTH = 256;

export type LibraryGroupValidationResult = { cellErrors: CellErrors; warning: string | null };

export type LibraryGroupMessages = {
  required: string;
  tooLong: (max: number) => string;
  notActive: string;
  unusedGroups: (groups: string[]) => string;
};

const DEFAULT_MESSAGES: LibraryGroupMessages = {
  required: 'Library group is required.',
  tooLong: max => `Library group must be ${max} characters or fewer.`,
  notActive: 'This library group is not on the plate for the current control count.',
  unusedGroups: groups => `No samples are mapped to ${groups.join(', ')} — that control's QC won't cover any sample.`,
};

/** Single source of truth for library_group correctness, feeding cell highlighting, a warning
 *  banner, and pre-submit blocking from one computation. `activeGroups === []` (run config not
 *  loaded yet) returns no errors, so that fallback state never renders a false wall of red cells. */
export function validateLibraryGroups(
  rows: InstrumentRunDataRow[],
  activeGroups: readonly string[],
  messages: LibraryGroupMessages = DEFAULT_MESSAGES,
): LibraryGroupValidationResult {
  const cellErrors: CellErrors = {};
  if (activeGroups.length === 0) {
    return { cellErrors, warning: null };
  }

  const activeSet = new Set(activeGroups);
  const usedGroups = new Set<string>();

  rows.forEach((row, i) => {
    // Trimmed once, then used consistently for every check below — a raw untrimmed value (e.g.
    // a CSV cell with trailing whitespace) would otherwise fail the "is active" membership check
    // even when it matches a real active group.
    const value = row.library_group?.trim();
    let message: string | null = null;
    if (!value) {
      message = messages.required;
    } else if (value.length > MAX_LIBRARY_GROUP_LENGTH) {
      message = messages.tooLong(MAX_LIBRARY_GROUP_LENGTH);
    } else if (!activeSet.has(value)) {
      message = messages.notActive;
    } else {
      usedGroups.add(value);
    }
    if (message) {
      cellErrors[i] = { ...cellErrors[i], library_group: message };
    }
  });

  const unusedActiveGroups = activeGroups.filter(g => !usedGroups.has(g));
  const warning = unusedActiveGroups.length > 0 ? messages.unusedGroups(unusedActiveGroups) : null;

  return { cellErrors, warning };
}

/** A single active group means every row is auto-filled and there is nothing to choose;
 *  zero means the run config hasn't loaded — either way, not user-editable. */
export const isLibraryGroupEditable = (activeLibraryGroups: readonly string[]): boolean =>
  activeLibraryGroups.length > 1;
